"use client";
import { useCallback, useEffect, useState } from "react";
import type { EventBinding } from "@/lib/api/events";
import {
  awardBindingKeys,
  persistAwardOperation,
  removeAwardOperation,
  restoreAwardOperations,
  type AwardOperation,
} from "@/lib/commands/award-operation-store";
export function useAwardRecovery(binding: EventBinding) {
  const [records, setRecords] = useState<AwardOperation[]>([]),
    [ready, setReady] = useState(false),
    [available, setAvailable] = useState(false);
  const read = useCallback(
    () => restoreAwardOperations(window.localStorage, binding),
    [binding],
  );
  const sync = useCallback(() => {
    try {
      setRecords(read());
      setAvailable(true);
    } catch {
      setAvailable(false);
    } finally {
      setReady(true);
    }
  }, [read]);
  useEffect(() => {
    const timer = setTimeout(sync, 0);
    const changed = (event: StorageEvent) => {
      if (
        !event.key ||
        awardBindingKeys(binding).some((prefix) =>
          event.key!.startsWith(prefix),
        )
      )
        sync();
    };
    window.addEventListener("storage", changed);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("storage", changed);
    };
  }, [binding, sync]);
  function save(row: AwardOperation) {
    try {
      persistAwardOperation(window.localStorage, binding, row);
      sync();
      return true;
    } catch {
      setAvailable(false);
      return false;
    }
  }
  function remove(key: string) {
    try {
      removeAwardOperation(window.localStorage, binding, key);
      sync();
      return true;
    } catch {
      setAvailable(false);
      return false;
    }
  }
  return { records, ready, available, read, sync, save, remove };
}
